export const COOKIE_CONSENT_VERSION = '2026-06';
export const COOKIE_CONSENT_STORAGE_KEY = 'aaa_cookie_consent';
export const COOKIE_CONSENT_EVENT = 'aaa:cookie-consent-changed';
export const COOKIE_PREFERENCES_OPEN_EVENT = 'aaa:cookie-preferences-open';

const isBrowser = () =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

/**
 * EU/UK (and broader European) detection via the browser timezone.
 * @returns {boolean}
 */
export const isRegulatedRegion = () => {
  if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) return true; // fail safe → strict
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    return /^Europe\//.test(timeZone) || timeZone === 'Atlantic/Canary';
  } catch {
    return true;
  }
};

/**
 * @returns {{analytics: boolean, version: string, ts: string} | null}
 */
export const getStoredCookieConsent = () => {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.version !== COOKIE_CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const hasAnalyticsConsent = () => {
  const stored = getStoredCookieConsent();
  if (stored) return stored.analytics === true;
  return !isRegulatedRegion();
};

export const needsCookieDecision = () => getStoredCookieConsent() === null;

const emitConsentChange = () => {
  if (!isBrowser()) return;
  try {
    window.dispatchEvent(new Event(COOKIE_CONSENT_EVENT));
  } catch {
    /* no-op */
  }
};

/**
 * Persist the user's choice.
 * @param {boolean} analyticsAllowed
 */
export const setCookieConsent = (analyticsAllowed) => {
  if (!isBrowser()) return;
  try {
    const record = {
      analytics: analyticsAllowed === true,
      version: COOKIE_CONSENT_VERSION,
      ts: new Date().toISOString(),
    };
    window.localStorage.setItem(
      COOKIE_CONSENT_STORAGE_KEY,
      JSON.stringify(record)
    );
    emitConsentChange();
  } catch {
    /* no-op */
  }
};

export const clearCookieConsent = () => {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
    emitConsentChange();
  } catch {
    /* no-op */
  }
};

export const openCookiePreferences = () => {
  if (!isBrowser()) return;
  try {
    window.dispatchEvent(new Event(COOKIE_PREFERENCES_OPEN_EVENT));
  } catch {
    /* no-op */
  }
};

export const subscribeToCookieConsentChanges = (callback) => {
  if (!isBrowser()) return () => {};
  const handler = () => callback();
  const storageHandler = (event) => {
    if (event.key === COOKIE_CONSENT_STORAGE_KEY || event.key === null) {
      callback();
    }
  };
  window.addEventListener(COOKIE_CONSENT_EVENT, handler);
  window.addEventListener('storage', storageHandler);
  return () => {
    window.removeEventListener(COOKIE_CONSENT_EVENT, handler);
    window.removeEventListener('storage', storageHandler);
  };
};

export const subscribeToCookiePreferencesOpen = (callback) => {
  if (!isBrowser()) return () => {};
  const handler = () => callback();
  window.addEventListener(COOKIE_PREFERENCES_OPEN_EVENT, handler);
  return () => window.removeEventListener(COOKIE_PREFERENCES_OPEN_EVENT, handler);
};
